import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Plus, Pencil, Trash2, Mail, MessageSquare, Copy, 
  Save, Eye, FileText, CheckCircle, Upload, Download, Clock, Calendar,
  MessageCircle, Bold, Italic, Type
} from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TemplateManagerTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importData, setImportData] = useState('');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'email',
    subject: '',
    message: '',
    is_active: true
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('tour_finder_token');
      const response = await fetch(`${API_URL}/api/admin/message-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.message) {
      toast.error('Please fill in template name and message');
      return;
    }

    try {
      const token = localStorage.getItem('tour_finder_token');
      const url = editingTemplate 
        ? `${API_URL}/api/admin/message-templates/${editingTemplate.id}`
        : `${API_URL}/api/admin/message-templates`;
      
      const response = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingTemplate ? 'Template updated!' : 'Template created!');
        loadTemplates();
        closeDialog();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save template');
      }
    } catch (error) {
      toast.error('Error saving template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      const token = localStorage.getItem('tour_finder_token');
      const response = await fetch(`${API_URL}/api/admin/message-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Template deleted');
        loadTemplates();
      }
    } catch (error) {
      toast.error('Error deleting template');
    }
  };

  const openCreateDialog = () => {
    setFormData({
      name: '',
      type: 'email',
      subject: '',
      message: '',
      is_active: true,
      schedule_enabled: false,
      schedule_trigger: 'before_booking',
      schedule_hours: 24
    });
    setEditingTemplate(null);
    setIsCreating(true);
  };

  const openEditDialog = (template) => {
    setFormData({
      name: template.name,
      type: template.type,
      subject: template.subject || '',
      message: template.message,
      is_active: template.is_active !== false,
      schedule_enabled: template.schedule_enabled || false,
      schedule_trigger: template.schedule_trigger || 'before_booking',
      schedule_hours: template.schedule_hours || 24
    });
    setEditingTemplate(template);
    setIsCreating(true);
  };

  const closeDialog = () => {
    setIsCreating(false);
    setEditingTemplate(null);
    setFormData({
      name: '',
      type: 'email',
      subject: '',
      message: '',
      is_active: true,
      schedule_enabled: false,
      schedule_trigger: 'before_booking',
      schedule_hours: 24
    });
  };

  // Bulk Import Functions
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      
      if (file.name.endsWith('.csv')) {
        // Parse CSV
        const lines = content.split('\n').filter(l => l.trim());
        if (lines.length < 2) {
          toast.error('CSV file must have headers and at least one data row');
          return;
        }
        
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const templates = [];
        
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',');
          const template = {};
          headers.forEach((header, idx) => {
            template[header] = values[idx]?.trim() || '';
          });
          templates.push(template);
        }
        
        setImportData(JSON.stringify(templates, null, 2));
      } else {
        // Assume JSON
        setImportData(content);
      }
      setShowImportDialog(true);
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  const handleImport = async () => {
    try {
      setImporting(true);
      let templates;
      
      try {
        templates = JSON.parse(importData);
        if (!Array.isArray(templates)) {
          templates = [templates];
        }
      } catch (e) {
        toast.error('Invalid JSON format');
        return;
      }

      const token = localStorage.getItem('tour_finder_token');
      const response = await fetch(`${API_URL}/api/admin/message-templates/bulk-import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ templates, format: 'json' })
      });

      const result = await response.json();
      
      if (response.ok) {
        toast.success(`Imported ${result.imported} templates!`);
        if (result.errors?.length > 0) {
          toast.warning(`${result.errors.length} errors occurred`);
        }
        loadTemplates();
        setShowImportDialog(false);
        setImportData('');
      } else {
        toast.error(result.detail || 'Import failed');
      }
    } catch (error) {
      toast.error('Error importing templates');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('tour_finder_token');
      const response = await fetch(`${API_URL}/api/admin/message-templates/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data.templates, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `templates-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`Exported ${data.count} templates`);
      }
    } catch (error) {
      toast.error('Error exporting templates');
    }
  };

  const copyTemplate = (template) => {
    const text = template.type === 'email' 
      ? `Subject: ${template.subject}\n\n${template.message}`
      : template.message;
    navigator.clipboard.writeText(text);
    toast.success('Template copied to clipboard!');
  };

  const insertVariable = (variable) => {
    setFormData(prev => ({
      ...prev,
      message: prev.message + ` {{${variable}}}`
    }));
  };

  const insertFormatting = (prefix, suffix) => {
    const textarea = document.querySelector('textarea[placeholder*="Enter your template"]');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = formData.message.substring(start, end);
      const newText = formData.message.substring(0, start) + prefix + (selectedText || 'text') + suffix + formData.message.substring(end);
      setFormData(prev => ({ ...prev, message: newText }));
    } else {
      // If no selection, just append at the end
      setFormData(prev => ({
        ...prev,
        message: prev.message + ` ${prefix}text${suffix}`
      }));
    }
  };

  const filteredTemplates = templates.filter(t => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'email') return t.type === 'email';
    if (activeFilter === 'sms') return t.type === 'sms';
    if (activeFilter === 'whatsapp') return t.type === 'whatsapp';
    return true;
  });

  const previewMessage = (message) => {
    return message
      .replace(/\{\{customer_name\}\}/g, 'John Doe')
      .replace(/\{\{customer_email\}\}/g, 'john.doe@email.com')
      .replace(/\{\{customer_phone\}\}/g, '+1 (787) 555-1234')
      .replace(/\{\{tour_title\}\}/g, 'El Yunque Rainforest Adventure')
      .replace(/\{\{tour_date\}\}/g, 'July 15, 2026')
      .replace(/\{\{tour_time\}\}/g, '9:00 AM')
      .replace(/\{\{guests\}\}/g, '2')
      .replace(/\{\{total_price\}\}/g, '$125.00')
      .replace(/\{\{booking_ref\}\}/g, 'BK-12345')
      .replace(/\{\{payment_status\}\}/g, 'Paid')
      .replace(/\{\{pickup_location\}\}/g, 'San Juan Marriott Resort')
      .replace(/\{\{pickup_time\}\}/g, '8:30 AM')
      .replace(/\{\{tour_duration\}\}/g, '4 hours')
      .replace(/\{\{special_requests\}\}/g, 'Vegetarian lunch')
      .replace(/\{\{company_name\}\}/g, 'Tour Finder Puerto Rico')
      .replace(/\{\{company_phone\}\}/g, '+1 (787) 555-0000');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Message Templates</h2>
          <p className="text-muted-foreground">Create and manage email & SMS templates for customer communication</p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".json,.csv"
            className="hidden"
          />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="gap-2">
            <Upload className="h-4 w-4" />
            Import
          </Button>
          <Button variant="outline" onClick={handleExport} className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
          <Button onClick={openCreateDialog} className="gap-2">
            <Plus className="h-4 w-4" />
            New Template
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'email', 'sms', 'whatsapp'].map(filter => (
          <Button
            key={filter}
            variant={activeFilter === filter ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter(filter)}
            className="capitalize"
          >
            {filter === 'email' && <Mail className="h-4 w-4 mr-1" />}
            {filter === 'sms' && <MessageSquare className="h-4 w-4 mr-1" />}
            {filter === 'whatsapp' && <MessageCircle className="h-4 w-4 mr-1" />}
            {filter === 'all' ? 'All Templates' : filter === 'whatsapp' ? 'WhatsApp' : filter}
          </Button>
        ))}
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading templates...</div>
      ) : filteredTemplates.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No templates yet</h3>
          <p className="text-muted-foreground mb-4">Create your first message template to get started</p>
          <Button onClick={openCreateDialog} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Template
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {filteredTemplates.map((template) => (
            <Card key={template.id} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    template.type === 'email' ? 'bg-blue-100' : 
                    template.type === 'whatsapp' ? 'bg-emerald-100' : 'bg-green-100'
                  }`}>
                    {template.type === 'email' 
                      ? <Mail className="h-5 w-5 text-blue-600" />
                      : template.type === 'whatsapp'
                      ? <MessageCircle className="h-5 w-5 text-emerald-600" />
                      : <MessageSquare className="h-5 w-5 text-green-600" />
                    }
                  </div>
                  <div>
                    <h3 className="font-semibold">{template.name}</h3>
                    <p className="text-xs text-muted-foreground capitalize">{template.type} Template</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap justify-end">
                  {template.schedule_enabled && (
                    <Badge variant="outline" className="text-xs border-purple-300 text-purple-700 gap-1">
                      <Clock className="h-3 w-3" />
                      {template.schedule_hours}h {template.schedule_trigger?.replace('_', ' ')}
                    </Badge>
                  )}
                  {template.is_active && (
                    <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                  )}
                </div>
              </div>
              
              {template.subject && (
                <p className="text-sm text-muted-foreground mb-2 truncate">
                  <strong>Subject:</strong> {template.subject}
                </p>
              )}
              
              <div className="text-sm text-muted-foreground line-clamp-2 mb-4">
                {template.type === 'whatsapp' ? (
                  <span 
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(
                        template.message
                          .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
                          .replace(/_([^_]+)_/g, '<em>$1</em>')
                          .replace(/~([^~]+)~/g, '<s>$1</s>')
                          .substring(0, 150) + (template.message.length > 150 ? '...' : '')
                      )
                    }} 
                  />
                ) : (
                  template.message
                )}
              </div>
              
              <div className="flex gap-2 pt-3 border-t">
                <Button variant="outline" size="sm" onClick={() => openEditDialog(template)} className="gap-1">
                  <Pencil className="h-3 w-3" />
                  Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => copyTemplate(template)} className="gap-1">
                  <Copy className="h-3 w-3" />
                  Copy
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleDelete(template.id)}
                  className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isCreating} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Template Name */}
            <div>
              <Label>Template Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Booking Confirmation"
                className="mt-1"
              />
            </div>
            
            {/* Template Type */}
            <div>
              <Label>Type</Label>
              <Select 
                value={formData.type} 
                onValueChange={(val) => setFormData(prev => ({ ...prev, type: val }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-purple-600" />
                      All Channels
                    </div>
                  </SelectItem>
                  <SelectItem value="email">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email Only
                    </div>
                  </SelectItem>
                  <SelectItem value="sms">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      SMS Only
                    </div>
                  </SelectItem>
                  <SelectItem value="whatsapp">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="h-4 w-4 text-emerald-600" />
                      WhatsApp Only
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                "All Channels" templates appear in Email, SMS, and WhatsApp options
              </p>
            </div>
            
            {/* Subject (Email only or All) */}
            {(formData.type === 'email' || formData.type === 'all') && (
              <div>
                <Label>Subject Line</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Email subject..."
                  className="mt-1"
                />
              </div>
            )}
            
            {/* WhatsApp Formatting Help */}
            {formData.type === 'whatsapp' && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <Label className="text-sm font-medium text-emerald-800 mb-2 flex items-center gap-2">
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp Formatting
                </Label>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <code className="bg-white px-2 py-1 rounded text-emerald-700">*bold*</code>
                    <span className="text-emerald-600">→ <strong>bold</strong></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-white px-2 py-1 rounded text-emerald-700">_italic_</code>
                    <span className="text-emerald-600">→ <em>italic</em></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-white px-2 py-1 rounded text-emerald-700">~strike~</code>
                    <span className="text-emerald-600">→ <s>strike</s></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-white px-2 py-1 rounded text-emerald-700">```mono```</code>
                    <span className="text-emerald-600">→ <code>mono</code></span>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => insertFormatting('*', '*')}
                    className="text-xs"
                  >
                    <Bold className="h-3 w-3 mr-1" /> Bold
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => insertFormatting('_', '_')}
                    className="text-xs"
                  >
                    <Italic className="h-3 w-3 mr-1" /> Italic
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => insertFormatting('~', '~')}
                    className="text-xs"
                  >
                    <Type className="h-3 w-3 mr-1" /> Strike
                  </Button>
                </div>
              </div>
            )}
            
            {/* Quick Variables */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Label className="text-sm font-medium text-blue-800 mb-2 block">
                Insert Variables (click to add)
              </Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Customer Name', var: 'customer_name' },
                  { label: 'Customer Email', var: 'customer_email' },
                  { label: 'Customer Phone', var: 'customer_phone' },
                  { label: 'Tour Title', var: 'tour_title' },
                  { label: 'Tour Date', var: 'tour_date' },
                  { label: 'Tour Time', var: 'tour_time' },
                  { label: 'Guests', var: 'guests' },
                  { label: 'Total Price', var: 'total_price' },
                  { label: 'Booking Ref', var: 'booking_ref' },
                  { label: 'Payment Status', var: 'payment_status' },
                  { label: 'Pickup Location', var: 'pickup_location' },
                  { label: 'Pickup Time', var: 'pickup_time' },
                  { label: 'Tour Duration', var: 'tour_duration' },
                  { label: 'Special Requests', var: 'special_requests' },
                  { label: 'Company Name', var: 'company_name' },
                  { label: 'Company Phone', var: 'company_phone' },
                ].map((v) => (
                  <button
                    key={v.var}
                    type="button"
                    onClick={() => insertVariable(v.var)}
                    className="px-3 py-1 bg-white border border-blue-300 rounded-full text-sm text-blue-700 hover:bg-blue-100 transition-colors"
                  >
                    + {v.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-blue-600 mt-2">
                Variables are automatically replaced with real customer/booking data when used
              </p>
            </div>
            
            {/* Message Content */}
            <div>
              <Label>Message *</Label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                rows={formData.type === 'email' ? 8 : formData.type === 'whatsapp' ? 6 : 4}
                className="w-full mt-1 px-3 py-2 border rounded-md"
                placeholder={
                  formData.type === 'email' 
                    ? "Enter your template message here..." 
                    : formData.type === 'whatsapp'
                    ? "Enter your template message here...\nUse *bold*, _italic_, ~strikethrough~ for formatting"
                    : "Enter your template message here (160 chars recommended)..."
                }
              />
              {formData.type === 'sms' && (
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.message.length}/160 characters ({Math.ceil(formData.message.length / 160) || 1} SMS segment{Math.ceil(formData.message.length / 160) > 1 ? 's' : ''})
                </p>
              )}
              {formData.type === 'whatsapp' && (
                <p className="text-xs text-emerald-600 mt-1">
                  WhatsApp supports up to 4096 characters per message
                </p>
              )}
            </div>
            
            {/* Preview */}
            <div className={`border rounded-lg p-4 ${formData.type === 'whatsapp' ? 'bg-emerald-50' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Eye className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Preview (with sample data)</Label>
              </div>
              <div className={`p-3 rounded border text-sm whitespace-pre-wrap ${
                formData.type === 'whatsapp' ? 'bg-[#e5f7ef] border-emerald-200' : 'bg-white'
              }`}>
                {formData.type === 'email' && formData.subject && (
                  <p className="font-semibold mb-2">Subject: {previewMessage(formData.subject)}</p>
                )}
                {formData.type === 'whatsapp' ? (
                  <div 
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(
                        previewMessage(formData.message)
                          .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
                          .replace(/_([^_]+)_/g, '<em>$1</em>')
                          .replace(/~([^~]+)~/g, '<s>$1</s>')
                          .replace(/```([^`]+)```/g, '<code class="bg-gray-200 px-1 rounded">$1</code>')
                          .replace(/\n/g, '<br/>')
                      )
                    }} 
                  />
                ) : (
                  previewMessage(formData.message) || <span className="text-muted-foreground italic">Enter a message to see preview</span>
                )}
              </div>
              {formData.type === 'whatsapp' && (
                <p className="text-xs text-emerald-600 mt-2">
                  <MessageCircle className="h-3 w-3 inline mr-1" />
                  This is how your message will look in WhatsApp
                </p>
              )}
            </div>
            
            {/* Auto-Send Scheduling */}
            <div className="border rounded-lg p-4 bg-purple-50 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-purple-600" />
                  <Label className="text-sm font-medium text-purple-800">Auto-Send Scheduling</Label>
                </div>
                <Switch
                  checked={formData.schedule_enabled}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, schedule_enabled: checked }))}
                />
              </div>
              
              {formData.schedule_enabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-purple-700">Trigger Event</Label>
                    <Select 
                      value={formData.schedule_trigger} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, schedule_trigger: v }))}
                    >
                      <SelectTrigger className="mt-1 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="before_booking">Before Booking Date</SelectItem>
                        <SelectItem value="after_booking">After Booking Created</SelectItem>
                        <SelectItem value="after_completion">After Tour Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-purple-700">Hours {formData.schedule_trigger === 'before_booking' ? 'Before' : 'After'}</Label>
                    <Input
                      type="number"
                      min="1"
                      max="168"
                      value={formData.schedule_hours}
                      onChange={(e) => setFormData(prev => ({ ...prev, schedule_hours: parseInt(e.target.value) || 24 }))}
                      className="mt-1 bg-white"
                    />
                  </div>
                </div>
              )}
              
              {formData.schedule_enabled && (
                <p className="text-xs text-purple-600">
                  <Calendar className="h-3 w-3 inline mr-1" />
                  This template will auto-send {formData.schedule_hours} hours {formData.schedule_trigger === 'before_booking' ? 'before the tour date' : formData.schedule_trigger === 'after_booking' ? 'after booking is made' : 'after tour completion'}
                </p>
              )}
            </div>
            
            {/* Active Toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="rounded"
              />
              <Label htmlFor="is_active" className="text-sm">Active (template will appear in compose dropdown)</Label>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleSave} className="gap-2">
              <Save className="h-4 w-4" />
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Templates</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template Data (JSON format)</Label>
              <Textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                rows={12}
                placeholder={`[
  {
    "name": "Welcome Message",
    "type": "email",
    "subject": "Welcome to Tour Finder!",
    "message": "Hi {{customer_name}}, welcome to our tour services!",
    "is_active": true,
    "schedule_enabled": false
  }
]`}
                className="mt-1 font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Required fields: name, message. Optional: type (email/sms), subject, is_active, schedule_enabled, schedule_trigger, schedule_hours
              </p>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>CSV Format:</strong> name,type,subject,message,is_active
              </p>
              <p className="text-xs text-blue-600 mt-1">
                First row must be headers. Use quotes for messages with commas.
              </p>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowImportDialog(false)}>Cancel</Button>
              <Button onClick={handleImport} disabled={importing || !importData.trim()} className="gap-2">
                <Upload className="h-4 w-4" />
                {importing ? 'Importing...' : 'Import Templates'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
